import { useEffect, useState } from 'react'
import { updateAppStatusBar } from '../../../utils/appBridge'
import RebalancePlan from './components/RebalancePlan'
import PostCountDownNonElite from './components/PostCountDownNonElite'
import PostCountDownElite from './components/PostCountDownElite'
import NoRebalanceNeeded from './components/NoRebalanceNeeded'
import CountdownView from './components/CountdownView'
import { StatusResponse, CountdownTime } from '../../../types/rebalance'

export const useCountdown = (targetDate: number | undefined) => {
  const calculateTimeRemaining = (endDate: number | undefined) => {
    if (!endDate) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }
    const difference = new Date(endDate).getTime() - new Date().getTime()
    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    }
  }

  const [timeRemaining, setTimeRemaining] = useState(calculateTimeRemaining(targetDate))

  useEffect(() => {
    if (!targetDate) return

    // Set initial time immediately
    setTimeRemaining(calculateTimeRemaining(targetDate))

    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(targetDate))
    }, 1000)

    return () => clearInterval(interval)
  }, [targetDate])

  const hasEnded =
    timeRemaining.days === 0 && timeRemaining.hours === 0 && timeRemaining.minutes === 0 && timeRemaining.seconds === 0

  return { ...timeRemaining, hasEnded }
}

// --- RENDER LOGIC COMPONENT --- //
const RenderContent = ({
  data,
  time,
  isUserElite
}: {
  data: StatusResponse
  time: CountdownTime
  isUserElite: boolean
}) => {
  const specialOffering = data.userSubscriptionDetails?.specialOffering ?? false
  const signupOffer = data.userSubscriptionDetails?.signupOffer ?? false
  const offerStartTime = data.userSubscriptionDetails?.offerStartTime
  const offerExpiryTime = data.userSubscriptionDetails?.offerExpiryTime
  const offerType = data.userSubscriptionDetails?.offerType
  const membershipFees = data.userSubscriptionDetails?.membershipFees?.amount ?? 0

  if (isUserElite) {
    if (data.rebalanceAvailable) {
      if (data.numberOfFundsToRebalance === 0 && data.numberOfFundsExcludedFromRebalance === 0) {
        return (
          <NoRebalanceNeeded
            missedGains={data.missedGains}
            isUserElite={isUserElite}
            riskProfile={data.riskProfile}
            rebalanceSnapshotDate={data.rebalanceSnapshotDate}
            planDurationInMonths={data.planDurationInMonths}
            rebalancePlanCount={data.rebalancePlanCount}
            throughFamilyAccess={data?.throughFamilyAccess}
            primaryUserName={data?.primaryUserName}
            secondaryUserName={data?.secondaryUserName}
            consentText={data?.familyConsentText}
            secondaryUserPhoneNumber={data?.secondaryUserPhoneNumber}
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees}
          />
        )
      } else {
        return (
          <RebalancePlan
            isUserElite={isUserElite}
            numberOfFundsToRebalance={data.numberOfFundsToRebalance}
            missedGains={data.missedGains}
            riskProfile={data.riskProfile}
            rebalanceAvailable={data.rebalanceAvailable}
            numberOfFundsExcludedFromRebalance={data.numberOfFundsExcludedFromRebalance}
            rebalanceSnapshotDate={data.rebalanceSnapshotDate}
            planDurationInMonths={data.planDurationInMonths}
            rebalancePlanCount={data.rebalancePlanCount}
            throughFamilyAccess={data?.throughFamilyAccess}
            primaryUserName={data?.primaryUserName}
            secondaryUserName={data?.secondaryUserName}
            consentText={data?.familyConsentText}
            secondaryUserPhoneNumber={data?.secondaryUserPhoneNumber}
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees}
          />
        )
      }
    } else {
      if (time.hasEnded) {
        return <PostCountDownElite firstPlanMonth={data.firstPlanMonth} />
      } else {
        return (
          <CountdownView
            time={time}
            firstPlanMonth={data.firstPlanMonth}
            nextPlanMonth={data.nextPlanMonth}
            isUserElite={isUserElite}
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees}
          />
        )
      }
    }
  } else {
    // return <RebalancePlan isUserElite={isUserElite} numberOfFundsToRebalance={data.numberOfFundsToRebalance} missedGains={data.missedGains} riskProfile={data.riskProfile} />
    if (data.rebalanceAvailable) {
      if (data.numberOfFundsToRebalance === 0 && data.numberOfFundsExcludedFromRebalance === 0) {
        return (
          <NoRebalanceNeeded
            missedGains={data.missedGains}
            isUserElite={isUserElite}
            riskProfile={data.riskProfile}
            rebalanceSnapshotDate={data.rebalanceSnapshotDate}
            planDurationInMonths={data.planDurationInMonths}
            rebalancePlanCount={data.rebalancePlanCount}
            throughFamilyAccess={data?.throughFamilyAccess}
            primaryUserName={data?.primaryUserName}
            secondaryUserName={data?.secondaryUserName}
            consentText={data?.familyConsentText}
            secondaryUserPhoneNumber={data?.secondaryUserPhoneNumber}
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees}
          />
        )
      } else {
        return (
          <RebalancePlan
            isUserElite={isUserElite}
            numberOfFundsToRebalance={data.numberOfFundsToRebalance}
            missedGains={data.missedGains}
            riskProfile={data.riskProfile}
            rebalanceAvailable={data.rebalanceAvailable}
            numberOfFundsExcludedFromRebalance={data.numberOfFundsExcludedFromRebalance}
            rebalanceSnapshotDate={data.rebalanceSnapshotDate}
            planDurationInMonths={data.planDurationInMonths}
            rebalancePlanCount={data.rebalancePlanCount}
            throughFamilyAccess={data?.throughFamilyAccess}
            primaryUserName={data?.primaryUserName}
            secondaryUserName={data?.secondaryUserName}
            consentText={data?.familyConsentText}
            secondaryUserPhoneNumber={data?.secondaryUserPhoneNumber}
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees}
          />
        )
      }
    } else {
      if (time.hasEnded) {
        return (
          <PostCountDownNonElite
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees}
          />
        )
      } else {
        return (
          <CountdownView
            time={time}
            firstPlanMonth={data.firstPlanMonth}
            nextPlanMonth={data.nextPlanMonth}
            isUserElite={isUserElite}
            specialOffering={specialOffering}
            signupOffer={signupOffer}
            offerStartTime={offerStartTime}
            offerExpiryTime={offerExpiryTime}
            offerType={offerType}
            membershipFees={membershipFees}
          />
        )
      }
    }
  }
}

// --- MAIN COMPONENT --- //
const Stopwatch = ({
  isRebalanceAvailable = false,
  isUserElite = false,
  rebalanceData,
  isLoading = false,
  isError = false
}: {
  isRebalanceAvailable?: boolean
  isUserElite?: boolean
  rebalanceData?: StatusResponse
  isLoading?: boolean
  isError?: boolean
}) => {
  const time = useCountdown(rebalanceData?.timeToRebalancePlanDrop)

  useEffect(() => {
    updateAppStatusBar('FFF4EB', 'light')
    updateAppStatusBar('FFF4EB', 'dark')
  }, [])

  // If no rebalanceData is provided, show a message
  if (!rebalanceData) {
    return <div className='px-5 pt-5 pb-5 bg-[#FFF4EB] h-full text-13 text-center'>No rebalance data available</div>
  }

  // Render loading or error states
  if (isLoading) {
    return <div className='px-5 pt-5 pb-5 bg-[#FFF4EB] h-full text-13 text-center'>Loading...</div>
  }

  if (isError) {
    return <div className='px-5 pt-5 pb-5 bg-[#FFF4EB] h-full text-13 text-center'>Something went wrong!</div>
  }

  return (
    <div
      style={{
        paddingTop: `calc(${isRebalanceAvailable ? '0px' : '16px'})`,
        paddingBottom: `calc(${isRebalanceAvailable ? '16px' : '0x'})`
      }}
      className={`px-5 bg-[#FFF4EB] `}
    >
      {rebalanceData?.userEligibleForRebalance && (
        <RenderContent data={rebalanceData} time={time} isUserElite={isUserElite} />
      )}
    </div>
  )
}

export default Stopwatch
